// =============================================================================
// ConfigLink — a "Config → Storage → Cooling flow" pointer that opens the tab.
//
//   Map each stage in <ConfigLink tab="storage" section="Cooling flow">Config → Storage → Cooling flow</ConfigLink>.
//
// The children are the words on screen today and stay exactly that; only the
// pointer becomes tappable. Rendered as a link ONLY when the viewer can open
// /config (useCanOpenPackhouseConfig — the route's own two gates). Anyone else,
// and everyone while the gate is still deciding, gets the same words as plain
// text: a link to a page the role can't open bounces them to Home, which is
// worse than text.
//
// An inline link in a sentence, so no 44px floor (WCAG 2.5.8's inline
// exception; the house pattern is `<Link className="underline">`). It keeps the
// surrounding colour, so an amber warning stays amber.
//
// `newTab` — for a pointer INSIDE a capture form or dialog. A same-tab link
// unmounts the form, and a <Modal>'s typed values are not on the page-leave
// registry, so they would go with no question. A new tab leaves the form
// standing (DuplicateNoteAdvisory's precedent on Gate Intake). Everywhere else
// it is a GuardedLink, which asks first only when the page has armed its guard.
// =============================================================================

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import GuardedLink from '@/components/GuardedLink'
import { useCanOpenPackhouseConfig } from '@/hooks/useCanOpenPackhouseConfig'
import { packhouseConfigHref, type PackhouseConfigTab } from '@/lib/packhouseConfigHref'
import { useReturnHereState } from '@/lib/returnTo'

export interface ConfigLinkProps {
  tab: PackhouseConfigTab
  /** The setting the pointer names inside the tab (see packhouseConfigHref). */
  section?: string
  /** Open in a new tab — for a pointer inside a capture form or dialog. */
  newTab?: boolean
  /** Classes the pointer carried as text; kept in both renderings. */
  className?: string
  children: ReactNode
}

const LINK_CLS = 'underline underline-offset-2 hover:decoration-2'

export default function ConfigLink({ tab, section, newTab = false, className, children }: ConfigLinkProps) {
  const canOpen = useCanOpenPackhouseConfig()
  // Config's Back returns to the page that pointed there (F19 UF-10). A new
  // tab has no history to return to, so only the same-tab link carries it.
  const returnHere = useReturnHereState()
  if (canOpen !== true) {
    return className ? <span className={className}>{children}</span> : <>{children}</>
  }
  const to = packhouseConfigHref(tab, section)
  return newTab
    ? <Link to={to} target="_blank" rel="noreferrer" className={clsx(className, LINK_CLS)}>
        {children}<span className="sr-only"> (opens in a new tab)</span>
      </Link>
    : <GuardedLink to={to} state={returnHere} className={clsx(className, LINK_CLS)}>{children}</GuardedLink>
}
